// Refreshes the localStorage session (util/session) from the server: the per-request state Rails
// renders into the page, plus the user's profile from /user/current_session.
//
// Both frontends run this — the Unpoly app through the [data-sync-session-data] compiler, the
// Inertia app through bootSessionSync() (stores/auth.svelte), fed from the sf-auth-state meta tag.

import session from '../util/session'
import union from 'lodash-es/union'
import { emit } from 'shared/javascripts/util/events'
import { getJson } from 'shared/javascripts/util/request'
import { safeRemoveSessionStorageItem } from 'shared/javascripts/chat_bot/util'

export interface SyncSessionData {
  lastCompletedStep: string | null
  signedIn: boolean
  userId: number | null
  academyPartnerName: string | null
  academyPartnerId: number | null
  academyFunction: string | null
  clearSession?: boolean
}

export interface SyncElement extends HTMLElement {
  waitForSync: Promise<unknown>
}

interface CurrentSessionResponse {
  userId: number
  userName: string | null
  role: string | null
  emailConfirmed: boolean
  educationLevel: string | null
  subjectAreaIdAndKey: string | null
  universityIdAndKey: string | null
  studyCourseIdAndKey: string | null
  studyStart: string | null
  gender: string | null
  schoolRegion: string | null
  schoolName: string | null
  schoolGrade: string | null
  schoolZipcode: string | null
  lastCompletedStep: string | null
  seenVideoIds: number[]
  bookmarkedVideoIds: number[]
}

export function syncSessionData(data: SyncSessionData): Promise<void> {
  session.signedIn = data.signedIn
  session.userId = data.userId
  session.academyPartnerName = data.academyPartnerName
  session.academyPartnerId = data.academyPartnerId
  session.academyFunction = data.academyFunction

  if (data.clearSession) {
    session.clear()
    clearInertiaHistoryKeys()
  }

  emit('app:session:synced')

  if (data.signedIn && data.lastCompletedStep !== 'from_authentication_provider') {
    // If user was just created from an external authentication provider, there
    // is no interesting session data to sync and only data to lose.
    return syncProfile()
  }

  return Promise.resolve()
}

// Also for a page that changed the profile and goes on without the full page load that would sync it.
export function syncProfile(): Promise<void> {
  const waitForSync = loadProfile()
  session.deferSyncUntilLoaded(waitForSync)
  return waitForSync
}

function clearInertiaHistoryKeys(): void {
  // mirrors historySessionStorageKeys in @inertiajs/core
  safeRemoveSessionStorageItem('historyKey')
  safeRemoveSessionStorageItem('historyIv')
}

async function loadProfile(): Promise<void> {
  const sessionData = await getJson<CurrentSessionResponse>('/user/current_session')
  if (!sessionData) {
    console.warn('Could not sync session data with server')
    return
  }

  const {
    userId,
    userName,
    role,
    emailConfirmed,
    educationLevel,
    subjectAreaIdAndKey,
    universityIdAndKey,
    studyCourseIdAndKey,
    studyStart,
    gender,
    schoolRegion,
    schoolName,
    schoolGrade,
    schoolZipcode,
    lastCompletedStep,
    bookmarkedVideoIds,
  } = sessionData

  Object.assign(session, {
    userId,
    userName,
    role,
    emailConfirmed,
    educationLevel,
    subjectAreaIdAndKey,
    universityIdAndKey,
    studyCourseIdAndKey,
    studyStart,
    gender,
    schoolRegion,
    schoolName,
    schoolGrade,
    schoolZipcode,
    lastCompletedStep,
    bookmarkedVideoIds,
  })

  session.seenVideoIds = union(session.seenVideoIds, sessionData.seenVideoIds)
}
